<template>
  <div>
    <!-- Header: Title + Action Buttons -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
      <div>
        <h2 class="text-lg font-semibold text-gray-800">Staff</h2>
        <p v-if="paginatorInfo" class="text-xs text-gray-500 mt-0.5">
          {{ paginatorInfo.total }} staff member{{ paginatorInfo.total !== 1 ? 's' : '' }}
        </p>
      </div>
      <div class="flex items-center gap-1.5">
        <slot name="actions" />
      </div>
    </div>

    <!-- Search Bar -->
    <div id="tour-teachers-search" class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
      <IconField iconPosition="left" class="w-full md:w-72">
        <InputIcon class="pi pi-search" />
        <InputText
          v-model="search"
          placeholder="Search staff..."
          class="w-full"
        />
      </IconField>

      <div class="flex items-center gap-3">
        <div class="flex items-center gap-1.5">
          <label for="staffStatus" class="text-gray-500 text-xs whitespace-nowrap">Status:</label>
          <Select
            id="staffStatus"
            v-model="statusFilter"
            :options="statusFilterOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="All"
            class="w-32"
            data-testid="staff-status-filter"
            @change="onStatusFilterChange"
          />
        </div>
        <div class="flex items-center gap-1.5">
          <label for="perPage" class="text-gray-500 text-xs whitespace-nowrap">Show:</label>
          <Select
            id="perPage"
            v-model="perPage"
            :options="[10, 20, 50, 100]"
            class="w-20"
            @change="onPerPageChange"
          />
        </div>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="staff.loading.value && !staff.data.value.length" class="flex justify-center items-center py-12">
      <Loader />
    </div>

    <!-- No staff -->
    <div v-else-if="!teachers.length" class="text-center py-10">
      <i class="pi pi-users text-2xl text-gray-300 mb-2"></i>
      <p class="text-gray-500 text-sm">No staff found.</p>
    </div>

    <!-- Staff Cards Grid -->
    <div v-else id="tour-teachers-grid" class="grid md:grid-cols-2 xl:grid-cols-3 gap-2">
      <div
        v-for="teacher in teachers"
        :key="teacher.id"
        class="group relative border border-gray-200 rounded-xl bg-white hover:shadow-md hover:border-gray-300 transition-all cursor-pointer overflow-hidden"
        @click="viewTeacher(teacher)"
      >
        <!-- Status accent bar -->
        <div
          class="absolute top-0 left-0 right-0 h-1"
          :class="isActive(teacher) ? 'bg-emerald-500' : 'bg-amber-400'"
        ></div>

        <div class="p-3">
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-2 min-w-0">
              <Avatar
                :label="getInitials(teacher.name)"
                class="!bg-primary !text-white font-bold shrink-0"
                shape="circle"
                size="normal"
              />
              <div class="min-w-0">
                <p class="font-semibold text-gray-800 text-sm truncate">{{ teacher.name }}</p>
                <p class="text-gray-500 text-xs truncate">{{ teacher.email }}</p>
              </div>
            </div>

            <!-- Kebab menu -->
            <div class="shrink-0">
              <Button
                icon="pi pi-ellipsis-v"
                severity="secondary"
                text
                rounded
                size="small"
                class="opacity-0 group-hover:opacity-100 transition-opacity"
                @click.stop="showMenu(teacher.id, $event)"
              />
              <Menu :model="getTeacherActions(teacher)" popup :ref="el => menuRefs[teacher.id] = el" />
            </div>
          </div>

          <!-- Role & Status -->
          <div class="mt-2 flex items-center gap-1.5 flex-wrap">
            <Tag
              :value="statusLabel(teacher)"
              :severity="isActive(teacher) ? 'success' : 'warn'"
              class="text-xs"
              data-testid="staff-status-tag"
            />
            <span v-if="formatRoles(teacher.roles)" class="text-xs text-gray-500 truncate">
              {{ formatRoles(teacher.roles) }}
            </span>
          </div>

          <!-- Department -->
          <div v-if="teacher.department" class="mt-1.5 flex items-center gap-1">
            <i class="pi pi-building text-gray-400" style="font-size: 0.65rem"></i>
            <span class="text-xs text-gray-500">{{ teacher.department.department_name }}</span>
          </div>

          <!-- Subjects -->
          <div v-if="teacher.subjects?.length" class="mt-2 flex flex-wrap gap-1">
            <span
              v-for="(group, idx) in teacher.subjects.slice(0, 3)"
              :key="idx"
              class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-primary/10 text-primary-700 border border-primary/20"
            >
              {{ group.subject.name }}
            </span>
            <span
              v-if="teacher.subjects.length > 3"
              class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600"
            >
              +{{ teacher.subjects.length - 3 }} more
            </span>
          </div>
        </div>

        <!-- Bottom action hint -->
        <div class="px-3 py-1.5 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <span class="text-xs text-gray-400">Click to view details</span>
          <i class="pi pi-chevron-right text-xs text-gray-400 group-hover:text-gray-600 transition-colors"></i>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="paginatorInfo" class="mt-3">
      <Paginator
        v-model:first="first"
        :rows="perPage"
        :totalRecords="paginatorInfo.total"
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
        @page="onPageChange"
      />
    </div>

    <!-- Remove Confirmation Dialog -->
    <Dialog v-model:visible="showRemoveDialog" modal :style="{ width: '28rem' }" :pt="{ root: { class: 'rounded-xl overflow-hidden' } }">
      <template #header>
        <div class="flex items-center gap-2">
          <div class="flex items-center justify-center w-8 h-8 rounded-full bg-red-50">
            <i class="pi pi-trash text-red-600 text-sm"></i>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 text-sm">Remove Staff</h3>
            <p class="text-xs text-gray-500">This action cannot be undone</p>
          </div>
        </div>
      </template>
      <div class="py-1.5 space-y-2">
        <p class="text-sm text-gray-700">
          Are you sure you want to remove <strong>{{ staffToRemove?.name }}</strong>?
          This severs their membership of this school: every role is revoked and they are removed from all
          assigned subjects and classes.
        </p>
        <p class="text-xs text-gray-500">
          Prefer <strong>Deactivate</strong> if they may return — it blocks access just the same but keeps the
          record, and you can reactivate them later.
        </p>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button label="Cancel" severity="secondary" text size="small" @click="showRemoveDialog = false" :disabled="isRemoving" />
          <Button label="Remove" severity="danger" icon="pi pi-trash" size="small" @click="confirmRemove" :loading="isRemoving" />
        </div>
      </template>
    </Dialog>

    <!-- Deactivate Confirmation Dialog (#225) -->
    <Dialog v-model:visible="showDeactivateDialog" modal :style="{ width: '28rem' }" :pt="{ root: { class: 'rounded-xl overflow-hidden' } }"
      data-testid="staff-deactivate-dialog">
      <template #header>
        <div class="flex items-center gap-2">
          <div class="flex items-center justify-center w-8 h-8 rounded-full bg-amber-50">
            <i class="pi pi-ban text-amber-600 text-sm"></i>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 text-sm">Deactivate Staff</h3>
            <p class="text-xs text-gray-500">They keep their record and can be reactivated later</p>
          </div>
        </div>
      </template>
      <div class="py-1.5 space-y-3">
        <p class="text-sm text-gray-700">
          Deactivate <strong>{{ staffToDeactivate?.name }}</strong>? Every role they hold in this school is revoked and
          they can no longer enter it. Reactivating restores entry but not their roles.
        </p>
        <div class="flex flex-col gap-1">
          <label for="deactivateReason" class="text-xs font-medium text-gray-600">Reason (optional, kept in the audit log)</label>
          <Textarea id="deactivateReason" v-model="deactivateReason" rows="3" autoResize class="w-full text-sm"
            placeholder="e.g. Contract ended, on extended leave…" data-testid="staff-deactivate-reason" />
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button label="Cancel" severity="secondary" text size="small" @click="showDeactivateDialog = false" :disabled="!!updatingId" />
          <Button label="Deactivate" severity="warn" icon="pi pi-ban" size="small" @click="confirmDeactivate"
            :loading="!!updatingId" data-testid="staff-deactivate-confirm" />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useStaff } from '@/composables/queries/useStaff';
import { useUpdateStaffStatus } from '@/composables/mutations/useUpdateStaffStatus';
import { DELETE_STAFF } from '@/graphql';
import { StaffStatus, type Staff, type PermissionGrant, type StaffFilter } from '@/types/composition-functions';
import Loader from '@/components/common/loader.vue';

const toast = useToast();
const router = useRouter();

const search = ref('');
const page = ref(1);
const perPage = ref(10);
const first = ref(0);

// Status filter (#225): the directory shows every membership by default.
const statusFilter = ref<StaffStatus | null>(null);
const statusFilterOptions: { label: string; value: StaffStatus | null }[] = [
  { label: 'All', value: null },
  { label: 'Active', value: StaffStatus.Active },
  { label: 'Inactive', value: StaffStatus.Inactive },
];

const staffVariables = ref<{ filter: StaffFilter; first: number; page: number }>({
  filter: {},
  first: perPage.value,
  page: page.value,
});

const buildFilter = (): StaffFilter => ({
  ...(search.value ? { query: search.value } : {}),
  ...(statusFilter.value ? { status: statusFilter.value } : {}),
});

const { staff } = useStaff({
  variables: staffVariables,
  autoFetch: true,
});

const teachers = computed(() => staff.data.value || []);
const paginatorInfo = computed(() => staff.paginatorInfo.value);

// school_staff.status is a plain string historically; anything but "active"
// is a deactivated membership.
const isActive = (teacher: Staff) => (teacher.status ?? '').toLowerCase() === StaffStatus.Active.toLowerCase();
const statusLabel = (teacher: Staff) => (isActive(teacher) ? 'Active' : 'Inactive');

// Deactivate / reactivate (#225)
const { updatingId, deactivate, reactivate } = useUpdateStaffStatus();
const showDeactivateDialog = ref(false);
const staffToDeactivate = ref<Staff | null>(null);
const deactivateReason = ref('');

function promptDeactivate(teacher: Staff) {
  staffToDeactivate.value = teacher;
  deactivateReason.value = '';
  showDeactivateDialog.value = true;
}

async function confirmDeactivate() {
  if (!staffToDeactivate.value) return;
  const ok = await deactivate(staffToDeactivate.value.id, deactivateReason.value);
  if (ok) {
    showDeactivateDialog.value = false;
    staffToDeactivate.value = null;
    staff.refetch?.();
  }
}

async function handleReactivate(teacher: Staff) {
  if (await reactivate(teacher.id)) {
    staff.refetch?.();
  }
}

const formatRoles = (roles: PermissionGrant[] | null | undefined): string => {
  if (!roles?.length) return '';
  const roleLabels = roles
    .filter((r) => r?.is_active !== false)
    .map(
      (r) =>
        r.permissionGroup?.name ||
        r.permission?.resource ||
        r.category ||
        null,
    )
    .filter((v): v is string => !!v);
  return Array.from(new Set(roleLabels)).join(', ');
};

// Remove staff
const showRemoveDialog = ref(false);
const staffToRemove = ref<Staff | null>(null);
const isRemoving = ref(false);
const { mutate: deleteStaff } = useMutation(DELETE_STAFF);

function promptRemove(teacher: Staff) {
  staffToRemove.value = teacher;
  showRemoveDialog.value = true;
}

async function confirmRemove() {
  if (!staffToRemove.value) return;
  try {
    isRemoving.value = true;
    await deleteStaff({ staffId: staffToRemove.value.id });
    toast.add({ severity: 'success', summary: 'Removed', detail: `${staffToRemove.value.name} has been removed.`, life: 3000 });
    showRemoveDialog.value = false;
    staffToRemove.value = null;
    staff.refetch?.();
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to remove staff.';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
  } finally {
    isRemoving.value = false;
  }
}

// Menu refs
const menuRefs: Record<string, { toggle: (e: Event) => void }> = {};
const showMenu = (id: string, event: Event) => {
  menuRefs[id]?.toggle(event);
};

const viewTeacher = (teacher: Staff) => {
  router.push({
    name: 'teacher-details',
    params: { id: teacher.id },
  });
};

// Debounced auto-search
let searchTimeout: ReturnType<typeof setTimeout> | null = null;
watch(search, (val) => {
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    page.value = 1;
    first.value = 0;
    staffVariables.value.page = 1;
    staffVariables.value.filter = buildFilter();
    staff.refetch?.();
  }, 400);
});

const onStatusFilterChange = () => {
  page.value = 1;
  first.value = 0;
  staffVariables.value.page = 1;
  staffVariables.value.filter = buildFilter();
  staff.refetch?.();
};

const onPerPageChange = () => {
  page.value = 1;
  first.value = 0;
  staffVariables.value.first = perPage.value;
  staffVariables.value.page = page.value;
  staff.refetch?.();
};

const onPageChange = (event: { page: number }) => {
  page.value = event.page + 1;
  staffVariables.value.page = page.value;
  staff.refetch?.();
};

function getTeacherActions(teacher: Staff) {
  return [
    {
      label: 'View Details',
      icon: 'pi pi-eye',
      command: () => viewTeacher(teacher),
    },
    {
      separator: true,
    },
    // Offboarding (#225): deactivate is the everyday action and sits above
    // the destructive remove.
    isActive(teacher)
      ? {
        label: 'Deactivate',
        icon: 'pi pi-ban',
        class: 'text-amber-700',
        command: () => promptDeactivate(teacher),
      }
      : {
        label: 'Reactivate',
        icon: 'pi pi-replay',
        class: 'text-emerald-700',
        command: () => handleReactivate(teacher),
      },
    {
      separator: true,
    },
    {
      label: 'Remove',
      icon: 'pi pi-trash',
      class: 'text-red-600',
      command: () => promptRemove(teacher),
    },
  ];
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
</script>
